import * as React from "react";

import * as ToggleGroupPrimitive from "@radix-ui/react-toggle-group";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "~/lib/utils";

// AIDEV-NOTE: SegmentedTabs — inset pill group for switching between related
// views of the same content (e.g., "Recording / Transcript" inside an action
// screen). Don't confuse with <Tabs> (Radix Tabs primitive), which is for
// full panel switching with associated content.
//
// Built on Radix ToggleGroup (single-select) so it handles arrow-key
// navigation and aria automatically.
//
// AIDEV-NOTE: design-system-v2 adds `kind` × `size` matrix:
//   - kind:  "icon" (icon only), "text" (text only), "iconText" (both, default)
//   - size:  "sm" / "md" (v2 spec); legacy "default" / "compact" remain
//
// kind/size are propagated to SegmentedTabsItem via React context so callers
// only configure them once on the root. Existing call sites that pass icon +
// label children continue to render exactly as before — the default kind is
// "iconText" and the default size keeps the original "default" density.

type SegmentedTabsKind = "icon" | "text" | "iconText";
type SegmentedTabsSize = "default" | "compact" | "sm" | "md";
// AIDEV-NOTE: `tone` selects the color language. "forest" is the original inset
// look; "crafted" is the Session Notes mockup-2 toggle — cream-200 track with a
// white active pill and terra-700 active label (icons inherit currentColor).
type SegmentedTabsTone = "forest" | "crafted";

interface SegmentedTabsContextValue {
    kind: SegmentedTabsKind;
    size: SegmentedTabsSize;
    tone: SegmentedTabsTone;
}

const SegmentedTabsContext =
    React.createContext<SegmentedTabsContextValue | null>(null);

const segmentedTabsRootVariants = cva(
    // AIDEV-NOTE: v2 unification — the outer pill keeps the original
    // forest-50 inset surface *plus* the v2 forest-200 hairline border so
    // every size shares the same color + border language. Active items
    // render as a white-filled, soft-shadowed inset on top.
    "grid gap-2 rounded-[14px] border border-brand-forest-200 bg-brand-forest-50 p-1.5",
    {
        variants: {
            size: {
                default: "",
                compact: "rounded-[10px] gap-1 p-1",
                sm: "inline-grid auto-cols-max grid-flow-col rounded-[10px] gap-0.5 p-[3px]",
                md: "inline-grid auto-cols-max grid-flow-col rounded-[10px] gap-0.5 p-[3px]",
            },
            columns: {
                "2": "grid-cols-2",
                "3": "grid-cols-3",
                "4": "grid-cols-4",
            },
            // AIDEV-NOTE: tone declared last so its color overrides win via
            // tailwind-merge over the base forest classes.
            tone: {
                forest: "",
                crafted: "border-brand-forest-100 bg-brand-cream-200",
            },
        },
        defaultVariants: { size: "default", columns: "3", tone: "forest" },
    },
);

const segmentedTabsItemVariants = cva(
    // AIDEV-NOTE: Base item padding bumped from `px-2 pt-2.5 pb-[9px]` to
    // `px-3 pt-3 pb-3.5` + gap-1 so the stacked icon+label+sub variant has
    // room to breathe at the new (text-sm / text-[13px]) sizes.
    // AIDEV-NOTE: Inactive text is forest-600 (was forest-700) to align
    // with the v2 sm/md spec so every density shares the same color story.
    "flex cursor-pointer flex-col items-center gap-1 rounded-[10px] bg-transparent px-3 pt-3 pb-3.5 text-brand-forest-600 transition-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-forest-800 focus-visible:ring-offset-1 disabled:cursor-default disabled:opacity-60 data-[state=on]:bg-white data-[state=on]:text-brand-forest-800 data-[state=on]:shadow-[0_2px_6px_-2px_rgba(27,67,50,0.15)]",
    {
        variants: {
            size: {
                default:
                    "[&>span:first-of-type]:font-medium [&[data-state=on]>span:first-of-type]:font-semibold",
                compact:
                    "rounded-[7px] px-2.5 py-2 text-xs font-medium data-[state=on]:font-semibold data-[state=on]:shadow-[0_1px_3px_rgba(27,67,50,0.1)]",
                // AIDEV-NOTE: justify-center + text-center keep labels
                // horizontally centered inside each pill so multi-word /
                // multi-character labels (e.g., "Transcript" vs "Notes")
                // align visually even when the grid auto-sizes cells.
                sm: "flex-row justify-center gap-1.5 rounded-[7px] px-2 py-[5px] text-center text-xs font-medium text-brand-forest-600 data-[state=on]:font-semibold data-[state=on]:text-brand-forest-800 data-[state=on]:shadow-[0_1px_2px_rgba(27,67,50,0.1)]",
                md: "flex-row justify-center gap-1.5 rounded-[7px] px-3.5 py-2 text-center text-sm font-medium text-brand-forest-600 data-[state=on]:font-semibold data-[state=on]:text-brand-forest-800 data-[state=on]:shadow-[0_1px_2px_rgba(27,67,50,0.1)]",
            },
            // AIDEV-NOTE: tone declared after size so its active color wins via
            // tailwind-merge. "crafted" recolors the active pill label to terra-700
            // (mockup 2); icons inherit currentColor so they tint to match.
            tone: {
                forest: "",
                crafted: "data-[state=on]:text-brand-terra-700",
            },
        },
        defaultVariants: { size: "default", tone: "forest" },
    },
);

// AIDEV-NOTE: ToggleGroup is a discriminated union over `type`. We pin to
// `single` and rebuild the prop surface from `HTMLAttributes<HTMLDivElement>` +
// the single-mode controlled props so consumers see one clean shape.
interface SegmentedTabsRootProps
    extends Omit<
            React.HTMLAttributes<HTMLDivElement>,
            "defaultValue" | "onChange" | "dir"
        >,
        VariantProps<typeof segmentedTabsRootVariants> {
    value?: string;
    defaultValue?: string;
    onValueChange?: (value: string) => void;
    disabled?: boolean;
    dir?: "ltr" | "rtl";
    /**
     * Layout variant. Defaults to `"iconText"` to preserve the existing
     * icon + label rendering used by current call sites.
     */
    kind?: SegmentedTabsKind;
    /**
     * Color language. Defaults to `"forest"` (the original inset look).
     * `"crafted"` is the Session Notes mockup-2 toggle.
     */
    tone?: SegmentedTabsTone;
}

const SegmentedTabs = React.forwardRef<
    React.ElementRef<typeof ToggleGroupPrimitive.Root>,
    SegmentedTabsRootProps
>(
    (
        {
            className,
            size,
            columns,
            kind = "iconText",
            tone = "forest",
            children,
            value,
            defaultValue,
            onValueChange,
            disabled,
            ...props
        },
        ref,
    ) => {
        // ToggleGroup `single` allows deselect; we want a permanent selection.
        // Treat empty value updates as no-op so the group stays sticky.
        const handleValueChange = (next: string) => {
            if (next) onValueChange?.(next);
        };
        const resolvedSize: SegmentedTabsSize = size ?? "default";
        const contextValue = React.useMemo<SegmentedTabsContextValue>(
            () => ({ kind, size: resolvedSize, tone }),
            [kind, resolvedSize, tone],
        );
        return (
            <SegmentedTabsContext.Provider value={contextValue}>
                <ToggleGroupPrimitive.Root
                    ref={ref}
                    type="single"
                    value={value}
                    defaultValue={defaultValue}
                    onValueChange={handleValueChange}
                    disabled={disabled}
                    className={cn(
                        segmentedTabsRootVariants({ size, columns, tone }),
                        className,
                    )}
                    {...props}
                >
                    {children}
                </ToggleGroupPrimitive.Root>
            </SegmentedTabsContext.Provider>
        );
    },
);
SegmentedTabs.displayName = "SegmentedTabs";

interface SegmentedTabsItemProps
    extends React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Item>,
        VariantProps<typeof segmentedTabsItemVariants> {
    icon?: React.ReactNode;
    label: React.ReactNode;
    sub?: React.ReactNode;
}

const SegmentedTabsItem = React.forwardRef<
    React.ElementRef<typeof ToggleGroupPrimitive.Item>,
    SegmentedTabsItemProps
>(({ className, size, icon, label, sub, ...props }, ref) => {
    // AIDEV-NOTE: kind/size flow from the SegmentedTabs root via context.
    // Explicit `size` on the item still wins for backwards compat with the
    // pre-v2 call sites that pass `size="compact"` directly on each item.
    const ctx = React.useContext(SegmentedTabsContext);
    const resolvedSize: SegmentedTabsSize = size ?? ctx?.size ?? "default";
    const kind: SegmentedTabsKind = ctx?.kind ?? "iconText";
    const tone: SegmentedTabsTone = ctx?.tone ?? "forest";
    const isV2 = resolvedSize === "sm" || resolvedSize === "md";
    const iconPx = resolvedSize === "sm" ? 14 : 18;
    const showIcon = kind === "icon" || kind === "iconText";
    const showLabelInline = kind === "text" || kind === "iconText";
    const iconOnlyMinWidth = resolvedSize === "sm" ? 26 : 34;

    return (
        <ToggleGroupPrimitive.Item
            ref={ref}
            className={cn(
                segmentedTabsItemVariants({ size: resolvedSize, tone }),
                kind === "icon" && isV2 && "justify-center px-0",
                className,
            )}
            style={
                kind === "icon" && isV2
                    ? { minWidth: iconOnlyMinWidth }
                    : undefined
            }
            {...props}
        >
            {isV2 ? (
                <>
                    {showIcon && icon && (
                        <span
                            className="inline-flex items-center justify-center"
                            style={{
                                width: iconPx,
                                height: iconPx,
                            }}
                            aria-hidden={kind === "iconText" ? true : undefined}
                        >
                            {icon}
                        </span>
                    )}
                    {showLabelInline ? (
                        <span>{label}</span>
                    ) : (
                        <span className="sr-only">{label}</span>
                    )}
                </>
            ) : resolvedSize === "compact" ? (
                label
            ) : (
                <>
                    {icon && (
                        // AIDEV-NOTE: 20px icon (was 17) — matches the bumped
                        // type scale (text-sm label, text-[13px] sub).
                        <span className="[&_svg]:h-5 [&_svg]:w-5">{icon}</span>
                    )}
                    <span className="text-sm">{label}</span>
                    {sub && (
                        <span className="text-[13px] text-brand-forest-600">
                            {sub}
                        </span>
                    )}
                </>
            )}
        </ToggleGroupPrimitive.Item>
    );
});
SegmentedTabsItem.displayName = "SegmentedTabsItem";

export {
    SegmentedTabs,
    SegmentedTabsItem,
    segmentedTabsItemVariants,
    segmentedTabsRootVariants,
};
export type { SegmentedTabsKind, SegmentedTabsSize, SegmentedTabsTone };
